import React from "react";
import { Inbox } from "lucide-react";
import { dict } from "../../i18n";
import { Badge } from "../ui/badge";
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from "../ui/table";
import { Skeleton } from "../ui/skeleton";

const L = dict.de; // admin UI is German
const COLUMNS = ["Name", "E-Mail", "Firma", "Typ", "Häufigkeit", "Engpass"];

const labelFor = (map, code) => (code && map[code]) || code || "—";

const SkeletonRows = () =>
  Array.from({ length: 5 }).map((_, i) => (
    <TableRow key={`skeleton-row-${i}`}>
      {COLUMNS.map((col) => (
        <TableCell key={`skeleton-cell-${i}-${col}`}><Skeleton className="h-4 w-full" /></TableCell>
      ))}
    </TableRow>
  ));

const EmptyRow = () => (
  <TableRow>
    <TableCell colSpan={COLUMNS.length}>
      <div className="flex flex-col items-center justify-center gap-2 py-14 text-center" data-testid="admin-empty-state">
        <span className="grid h-11 w-11 place-items-center rounded-full bg-[var(--surface-2)] text-[var(--slate)]">
          <Inbox className="h-5 w-5" />
        </span>
        <div className="text-sm font-medium text-[var(--ink)]">Noch keine Anmeldungen</div>
        <div className="text-xs text-[var(--slate)]">Neue Einträge erscheinen hier automatisch.</div>
      </div>
    </TableCell>
  </TableRow>
);

const DataRows = ({ items }) =>
  items.map((it, idx) => (
    <TableRow key={it.id || it.email} className="hover:bg-[var(--surface-2)]/40" data-testid={`admin-row-${idx}`}>
      <TableCell className="font-medium text-[var(--ink)]">{it.name}</TableCell>
      <TableCell>
        <a href={`mailto:${it.email}`} className="text-[var(--blue)] hover:underline">{it.email}</a>
      </TableCell>
      <TableCell className="text-[var(--ink)]">{it.company}</TableCell>
      <TableCell><Badge variant="secondary" className="font-normal">{labelFor(L.companyTypes, it.companyType)}</Badge></TableCell>
      <TableCell className="text-[var(--slate)]">{labelFor(L.frequencies, it.bidFrequency)}</TableCell>
      <TableCell className="text-[var(--slate)]">{labelFor(L.bottlenecks, it.bottleneck)}</TableCell>
    </TableRow>
  ));

/** Renders the appropriate table body without nested ternaries. */
const TableBodyContent = ({ items, loading }) => {
  if (loading) return <SkeletonRows />;
  if (!items || items.length === 0) return <EmptyRow />;
  return <DataRows items={items} />;
};

export const SignupsTable = ({ items, loading }) => (
  <div className="mt-6 overflow-hidden rounded-2xl border border-[var(--border)] bg-white shadow-md-soft">
    <div className="overflow-x-auto">
      <Table data-testid="admin-signups-table">
        <TableHeader>
          <TableRow className="bg-[var(--surface-2)]/60">
            {COLUMNS.map((col) => (
              <TableHead key={col} className="text-[var(--slate)]">{col}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableBodyContent items={items} loading={loading} />
        </TableBody>
      </Table>
    </div>
  </div>
);
